import React, { useMemo, useState, useEffect } from "react";
import Paginacion from "../shared/Paginacion";
import BarraFiltros from "../shared/BarraFiltros";
import { API_URL, getAccessToken } from "../../services/api"; // helpers oficiales
import { LABELS, formatDuracionMin, calcularResumenDia, detectarDescansoConstante } from "../../utils/tiempos";
import KpiRow from "../shared/KpiRow";

// ✅ Igual que en empleado: intenta sacar un label de ubicación de un objeto log/fichaje
function pickLabel(obj) {
  if (!obj || typeof obj !== "object") return null;
  return (
    obj.loc_label ||
    obj.locLabel ||
    obj.label ||
    (obj.loc && (obj.loc.label || obj.loc.name)) ||
    null
  );
}

// ✅ Fallback: si no hay label, pero hay lat/lng, mostramos coords (opcional y ligero)
function pickCoords(obj) {
  if (!obj || typeof obj !== "object") return null;
  const lat = obj.lat ?? obj.latitude ?? obj.geo_lat;
  const lng = obj.lng ?? obj.longitude ?? obj.geo_lng;
  if (lat == null || lng == null) return null;
  const nLat = Number(lat);
  const nLng = Number(lng);
  if (!Number.isFinite(nLat) || !Number.isFinite(nLng)) return null;
  return `${nLat.toFixed(5)}, ${nLng.toFixed(5)}`;
}

export default function LogsTab({ logs, pausaMin = 0, pausaEnabled = false }) {
  // Filtros
  const [busqueda, setBusqueda] = useState("");
  const [rangoSeleccionado, setRangoSeleccionado] = useState("todos");
  const [fechasPersonalizadas, setFechasPersonalizadas] = useState({ inicio: "", fin: "" });

  // Paginación
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(5);

  // Anim
  const [fadeOn, setFadeOn] = useState(true);
  useEffect(() => {
    setFadeOn(false);
    const t = setTimeout(() => setFadeOn(true), 0);
    return () => clearTimeout(t);
  }, [busqueda, rangoSeleccionado, fechasPersonalizadas, pagina, porPagina]);

  const logsArray = Array.isArray(logs) ? logs : [];
  const parseTs = (ts) => {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };

  // Filtrado
  const logsFiltrados = useMemo(() => {
    const q = (busqueda || "").toLowerCase().trim();
    const now = Date.now();

    const byText = (l) => {
      if (!q) return true;
      return (
        (l.usuario_email || "").toLowerCase().includes(q) ||
        (l.tipo || "").toLowerCase().includes(q) ||
        (l.detalle || "").toLowerCase().includes(q) ||
        (l.motivo || "").toLowerCase().includes(q)
      );
    };

    const byFecha = (l) => {
      const ts = parseTs(l.timestamp);
      switch (rangoSeleccionado) {
        case "24h":
          return now - ts <= 24 * 60 * 60 * 1000;
        case "5d":
          return now - ts <= 5 * 24 * 60 * 60 * 1000;
        case "7d":
          return now - ts <= 7 * 24 * 60 * 60 * 1000;
        case "14d":
          return now - ts <= 14 * 24 * 60 * 60 * 1000;
        case "personalizado": {
          if (!fechasPersonalizadas.inicio && !fechasPersonalizadas.fin) return true;
          const ini = fechasPersonalizadas.inicio
            ? new Date(fechasPersonalizadas.inicio).getTime()
            : -Infinity;
          const fin = fechasPersonalizadas.fin
            ? new Date(fechasPersonalizadas.fin).getTime() + (23 * 60 * 60 + 59 * 60 + 59) * 1000
            : Infinity;
          return ts >= ini && ts <= fin;
        }
        default:
          return true;
      }
    };

    return logsArray
      .filter(byText)
      .filter(byFecha)
      .sort((a, b) => parseTs(b.timestamp) - parseTs(a.timestamp));
  }, [logsArray, busqueda, rangoSeleccionado, fechasPersonalizadas]);

  // Agrupación (✅ ahora también guarda ubicación por evento e intervalo)
  const agrupados = useMemo(
    () => agruparLogsConIntervalos(logsFiltrados, pausaMin, pausaEnabled),
    [logsFiltrados, pausaMin, pausaEnabled]
  );

  // Paginación
  const totalPaginas = Math.ceil(agrupados.length / porPagina) || 1;
  const desde = (pagina - 1) * porPagina;
  const visibles = agrupados.slice(desde, desde + porPagina);

  // Microcopy
  const fechaLabel =
    rangoSeleccionado === "24h"
      ? "Últimas 24h"
      : rangoSeleccionado === "5d"
      ? "Últimos 5 días"
      : rangoSeleccionado === "7d"
      ? "Últimos 7 días"
      : rangoSeleccionado === "14d"
      ? "Últimos 14 días"
      : rangoSeleccionado === "personalizado"
      ? fechasPersonalizadas.inicio && fechasPersonalizadas.fin
        ? `Personalizado: ${fmt(fechasPersonalizadas.inicio)} → ${fmt(fechasPersonalizadas.fin)}`
        : "Personalizado"
      : "Todo";

  const microcopy = `Mostrando: ${fechaLabel} · ${logsFiltrados.length} resultado${
    logsFiltrados.length === 1 ? "" : "s"
  }${busqueda ? ` · Filtro: “${busqueda}”` : ""}`;

  const resumenGlobal = useMemo(() => {
    const dias = agrupados.map((d) => ({
      descansoMin: Number(d.descansoMin) || 0,
      efectivoMin: Number(d.tiempoEfectivoMin) || 0,
      jornadaMin: Number(d.jornadaMin) || 0,
    }));
    const totalDias = dias.length;
    const totalEfectivo = dias.reduce((acc, d) => acc + (d.efectivoMin || 0), 0);
    const descInfo = detectarDescansoConstante(dias, { toleranceMin: 5 });
    return { totalDias, totalEfectivo, descInfo };
  }, [agrupados]);

  // Base URL (API_URL ya termina en /api)
  const API_BASE = API_URL.replace(/\/+$/, "");

  // Exportar (POST con Bearer + payload filtrado)
  const exportarLogs = async (formato = "csv") => {
    try {
      const token = getAccessToken();
      if (!token) {
        alert("No hay sesión. Inicia sesión de nuevo.");
        return;
      }

      const res = await fetch(
        `${API_BASE}/logs/exportar_logs?formato=${encodeURIComponent(formato)}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(logsFiltrados), // lo que ves en pantalla
        }
      );

      if (!res.ok) {
        let errText = "";
        try {
          errText = await res.text();
        } catch {}
        console.error("Export error:", res.status, errText);
        alert(errText || `❌ Error al exportar (${res.status})`);
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ext =
        formato === "xlsx"
          ? "xlsx"
          : formato === "pdf"
          ? "pdf"
          : formato === "json"
          ? "json"
          : "csv";
      a.href = url;
      a.download = `logs.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("❌ Error exportando logs:", err);
      alert(err?.message || "❌ Error al exportar");
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="sr-only">Registros de fichajes y eventos</h2>

      <BarraFiltros
        microcopy={microcopy}
        placeholderBusqueda="Buscar por usuario, tipo, detalle o motivo…"
        busqueda={busqueda}
        setBusqueda={(v) => {
          setBusqueda(v);
          setPagina(1);
        }}
        rangoSeleccionado={rangoSeleccionado}
        setRangoSeleccionado={(v) => {
          setRangoSeleccionado(v);
          setPagina(1);
        }}
        fechasPersonalizadas={fechasPersonalizadas}
        setFechasPersonalizadas={(v) => {
          setFechasPersonalizadas(v);
          setPagina(1);
        }}
        porPagina={porPagina}
        setPorPagina={(n) => {
          setPorPagina(n);
          setPagina(1);
        }}
      />

      {agrupados.length > 0 && (() => {
        const descInfo = resumenGlobal.descInfo || {};
        const descTotalTxt = formatDuracionMin(descInfo.totalDescansoMin || 0);
        const descConstante = !!descInfo.esConstante;
        const descLinea = descConstante
          ? `${descInfo.diasConDescanso} x ${formatDuracionMin(descInfo.valorBaseMin || 0)} = ${descTotalTxt}`
          : descTotalTxt;

        let descExtra = "";
        if (!descConstante && (descInfo.diasConDescanso || 0) >= 2) {
          const mediaTxt = formatDuracionMin(descInfo.mediaDescansoMin || 0);
          const minTxt = formatDuracionMin(descInfo.minDescansoMin || 0);
          const maxTxt = formatDuracionMin(descInfo.maxDescansoMin || 0);
          descExtra = `Media descanso/día: ${mediaTxt}`;
          if (minTxt !== maxTxt) descExtra += ` · Min-Max: ${minTxt}-${maxTxt}`;
        }

        const mediaEfectiva =
          resumenGlobal.totalDias >= 2
            ? formatDuracionMin(Math.round(resumenGlobal.totalEfectivo / resumenGlobal.totalDias))
            : "";

        return (
          <div className="rounded-2xl border border-slate-200 bg-white/70 backdrop-blur px-4 py-3 shadow-sm">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Resumen global</div>
            <div className="mt-2 flex flex-wrap gap-6">
              <div>
                <div className="text-xs text-slate-500">{LABELS.EFECTIVO} total</div>
                <div className="text-base font-semibold text-emerald-700">
                  {formatDuracionMin(resumenGlobal.totalEfectivo)}
                </div>
                {mediaEfectiva && (
                  <div className="text-xs text-slate-500">Media efectiva/día: {mediaEfectiva}</div>
                )}
              </div>
              <div>
                <div className="text-xs text-slate-500">{LABELS.DESCANSO} total</div>
                <div className="text-sm font-semibold text-slate-700">{descLinea}</div>
                {descExtra && <div className="text-xs text-slate-500">{descExtra}</div>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Listado */}
      <ul
        className="text-xs sm:text-sm space-y-3 sm:space-y-4"
        style={{
          opacity: fadeOn ? 1 : 0,
          transform: fadeOn ? "translateY(0)" : "translateY(4px)",
          transition: "opacity 200ms ease, transform 200ms ease",
        }}
      >
        {visibles.length === 0 ? (
          <li className="p-4 rounded-2xl border border-dashed border-gray-300 bg-white/60 text-center text-gray-600 italic">
            No se encontraron registros con los filtros aplicados.
          </li>
        ) : (
          visibles.map((entry, i) => (
            <li
              key={`${entry.usuario}-${entry.fecha}-${i}`}
              className="p-3 sm:p-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                <p className="text-sm sm:text-base font-bold text-blue-800">{entry.usuario}</p>
                <p className="text-gray-700 text-xs sm:text-sm">{entry.fecha}</p>
              </div>

              <ul className="mt-2 sm:mt-3 pl-2 sm:pl-3 text-gray-700 space-y-1 border-l-2 border-blue-200">
                {entry.intervalos.map((t, idx) => (
                  <li key={idx} className="pl-2">
                    <span className="inline-flex flex-wrap items-center gap-1 sm:gap-2">
                      <span aria-hidden>🕒</span>

                      <strong>{t.entrada}</strong>
                      {!!t.locEntrada && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-[11px]"
                          title="Ubicación de la entrada"
                        >
                          📍 {t.locEntrada}
                        </span>
                      )}

                      <span className="opacity-60">→</span>

                      <strong>{t.salida}</strong>
                      {!!t.locSalida && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-[11px]"
                          title="Ubicación de la salida"
                        >
                          📍 {t.locSalida}
                        </span>
                      )}

                      <span className="text-gray-500 italic ml-1 sm:ml-2">
                        Tramo: {t.duracion}
                      </span>

                      {(t.manualEntrada || t.manualSalida) && (
                        <span
                          className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[11px]"
                          title="Registro manual"
                        >
                          ✍️ manual
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-2 sm:mt-3">
                <KpiRow
                  jornada={entry.jornada}
                  descanso={entry.descanso}
                  efectivo={entry.tiempoEfectivo}
                />
                {entry.diaAbierto && (
                  <p className="text-amber-700 mt-1 text-xs sm:text-sm">
                    Dia abierto - Descanso no aplicado
                  </p>
                )}
              </div>
            </li>
          ))
        )}
      </ul>

      {/* Paginación */}
      {totalPaginas > 1 && (
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs sm:text-sm text-gray-700">
            Página {pagina} de {totalPaginas}
          </span>
          <Paginacion
            pagina={pagina}
            totalPaginas={totalPaginas}
            totalItems={agrupados.length}
            porPagina={porPagina}
            onAnterior={() => setPagina((p) => Math.max(1, p - 1))}
            onSiguiente={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
            onGoToPage={(n) => setPagina(n)}
          />
        </div>
      )}

      {/* Exportaciones */}
      <div className="pt-4 border-t border-gray-200 flex flex-wrap gap-2 sm:gap-3">
        <button
          onClick={() => exportarLogs("csv")}
          className="px-4 sm:px-5 py-2 rounded-2xl bg-gradient-to-r from-indigo-500 to-indigo-600 text-white font-semibold shadow-md hover:shadow-lg hover:scale-[1.03] transition-all duration-200"
        >
          📄 CSV
        </button>
        <button
          onClick={() => exportarLogs("pdf")}
          className="px-4 sm:px-5 py-2 rounded-2xl bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold shadow-md hover:shadow-lg hover:scale-[1.03] transition-all duration-200"
        >
          🧾 PDF
        </button>
        <button
          onClick={() => exportarLogs("json")}
          className="px-4 sm:px-5 py-2 rounded-2xl bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold shadow-md hover:shadow-lg hover:scale-[1.03] transition-all duration-200"
        >
          🧠 JSON
        </button>
        <button
          onClick={() => exportarLogs("xlsx")}
          className="px-4 sm:px-5 py-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold shadow-md hover:shadow-lg hover:scale-[1.03] transition-all duration-200"
        >
          📊 XLSX
        </button>
      </div>
    </div>
  );
}

/* Utils */
function formatHora(date) {
  return new Date(date)
    .toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", hour12: false })
    .replace(/\u200E/g, "");
}

function agruparLogsConIntervalos(logs, pausaMin, pausaEnabled) {
  const agrupados = {};

  logs.forEach((log) => {
    const user = log.usuario_email;
    const fecha = new Date(log.timestamp).toLocaleDateString("es-ES");
    const time = new Date(log.timestamp);
    const tipo = (log.tipo || "").toLowerCase();
    const isManual = !!log.is_manual;
    const motivo = log.motivo || "";

    // ✅ ubicación (label preferente; si no, coords)
    const locLabel = pickLabel(log) || pickCoords(log) || "";

    if (!user || !fecha || !tipo) return;
    if (!agrupados[user]) agrupados[user] = {};
    if (!agrupados[user][fecha]) agrupados[user][fecha] = [];

    agrupados[user][fecha].push({ tipo, time, isManual, motivo, locLabel });
  });

  const resultado = [];

  for (const usuario in agrupados) {
    for (const fecha in agrupados[usuario]) {
      const logsDia = agrupados[usuario][fecha].sort((a, b) => a.time - b.time);
      const intervalos = [];
      const intervalosMeta = [];

      let entrada = null,
        entradaManual = false,
        motivoEntrada = "",
        locEntrada = "";

      logsDia.forEach(({ tipo, time, isManual, motivo, locLabel }) => {
        if (tipo === "entrada") {
          entrada = time;
          entradaManual = isManual;
          motivoEntrada = motivo;
          locEntrada = locLabel || "";
        } else if (tipo === "salida" && entrada) {
          const durMs = time - entrada;
          const durMin = Math.max(0, Math.floor(durMs / 60000));

          intervalos.push({
            entrada: formatHora(entrada),
            salida: formatHora(time),
            duracion: formatDuracionMin(durMin),
            duracionMin: durMin,
            manualEntrada: entradaManual,
            manualSalida: isManual,
            motivoEntrada: motivoEntrada || "",
            motivoSalida: motivo || "",
            locEntrada: locEntrada || "",
            locSalida: locLabel || "",
          });
          intervalosMeta.push({ inicio: entrada, fin: time, duracionMin: durMin });

          entrada = null;
          motivoEntrada = "";
          locEntrada = "";
        }
      });

      if (entrada) {
        intervalosMeta.push({ inicio: entrada, fin: null, duracion: 0 });
      }
      const resumenDia = calcularResumenDia(intervalosMeta, {
        pausaFijaMin: Number(pausaMin) || 0,
        pausaFijaEnabled: !!pausaEnabled,
      });

      resultado.push({
        usuario,
        fecha,
        intervalos,
        jornada: formatDuracionMin(resumenDia.jornadaMin),
        descanso: formatDuracionMin(resumenDia.descansoMin),
        tiempoEfectivo: formatDuracionMin(resumenDia.efectivoMin),
        jornadaMin: resumenDia.jornadaMin,
        descansoMin: resumenDia.descansoMin,
        tiempoEfectivoMin: resumenDia.efectivoMin,
        diaAbierto: resumenDia.diaAbierto,
      });
    }
  }

  return resultado;
}

function fmt(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
